import type React from 'react';
import { useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { PortalWrapperProps } from '../schema/PortalWrapper.types';

export const PortalWrapper: React.FunctionComponent<PortalWrapperProps> = (props): JSX.Element => {
    const { divOrIdToAttachTo, children, classNameForDiv, onUnmount, onError } = props;

    const actualParentDivRef = useRef<HTMLElement | null>(
        isString(divOrIdToAttachTo) ? document.createElement('div') : null
    );
    const classNameForDivRef = useRef<string | undefined>(classNameForDiv);
    const divIdToAttachToRef = useRef<string | null>(
        isString(divOrIdToAttachTo) ? divOrIdToAttachTo : null
    );
    /**
     * From https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
     * We are using useLayoutEffect because it works the same way as componentDidMount and componentDidUpdate.
     * The code runs immediately after the DOM has been updated, but before the browser has had a chance to "paint" those changes
     * and the user doesn't actually see the updates until after the browser has repainted, which is intended.
     */
    useLayoutEffect(() => {
        const actualParentDiv = actualParentDivRef.current;
        const classNameForDiv = classNameForDivRef.current;
        if (divIdToAttachToRef.current && actualParentDiv) {
            actualParentDiv.id = divIdToAttachToRef.current;
            actualParentDiv.className = classNameForDiv;
            actualParentDiv &&
                !document.getElementById(actualParentDiv.id) &&
                document.body.appendChild(actualParentDiv);
        } else if (classNameForDiv && !divIdToAttachToRef.current) {
            actualParentDiv.className = classNameForDiv;
        }

        return () => {
            try {
                actualParentDiv && document.body.removeChild(actualParentDiv);
                onUnmount?.();
            } catch (error) {
                onError?.(error);
            }
        };
    }, [onError, onUnmount]);

    return ReactDOM.createPortal(
        children,
        actualParentDivRef.current || (divOrIdToAttachTo as Element)
    );
};

const isString = (divOrIdToAttachTo: string | HTMLElement): divOrIdToAttachTo is string =>
    typeof divOrIdToAttachTo === 'string';

export default PortalWrapper;
